import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Menu, Tooltip } from "antd";
import {
  AppstoreOutlined,
  FlagOutlined,
  HomeOutlined,
  LeftOutlined,
  LogoutOutlined,
  MailOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { logout } from "../auth";
import useCurrentUserData from "../hooks/useCurrentUserData";
import useRewardsCatalog from "../hooks/useRewardsCatalog";
import { dashboardPalette } from "../theme";
import profileIcon from "../assets/profile.png";
import RewardedProfileDisplay from "./RewardedProfileDisplay";
import { useAuth } from "../contexts/AuthContext";
import { isAdminEmail } from "../utils/adminAuth";
import { mergeRewardsWithUserState } from "../utils/rewards";
import { buildGmailComposeUrl, MANAGEMENT_EMAIL } from "../utils/mail";
import { MessageOutlined } from "@ant-design/icons";
import { Badge } from "antd";
import { useMyChats, usePublicProfileSync } from "../hooks/useChat";

const BASE_MENU_ITEMS = [
  { id: "dashboard", label: "Inicio", icon: HomeOutlined, path: "/dashboard" },
  
  {
    id: "redeem",
    label: "Progreso y Recompensas",
    icon: FlagOutlined,
    path: "/redeem",
  },
  { id: "skills", label: "Skills", icon: AppstoreOutlined, path: "/skills" },
  { id: "chat", label: "Mensajes", icon: MessageOutlined, path: "/chat" },
  { id: "profile", label: "Perfil", icon: UserOutlined, path: "/profile" },
  { id: "comunidad", label: "Comunidad", icon: TeamOutlined, path: "/comunidad" },
];

const ADMIN_MENU_ITEM = {
  id: "admin",
  label: "Admin",
  icon: SettingOutlined,
  path: "/admin",
};

interface SidebarProps {
  onMobileClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  onMobileClose,
  collapsed = false,
  onToggleCollapse,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useCurrentUserData();
  const { catalog } = useRewardsCatalog();
  const { user } = useAuth();
  const sidebarAvatarSrc =
    userData && userData.PortraitImg !== undefined
      ? userData.PortraitImg || profileIcon
      : profileIcon;

  // Las recompensas "reales" viven en el catálogo compartido; acá solo
  // juntamos esa definición con el estado (reclamado/equipado) del usuario.
  const mergedRewards = useMemo(
    () => mergeRewardsWithUserState(catalog, userData?.Recompensas),
    [catalog, userData?.Recompensas],
  );

  // El botón de Admin solo aparece para la cuenta admin@admin.com.
  // Esto es solo estético: la ruta /admin ya está protegida aparte por
  // AdminRoute, y los datos por las reglas de Firestore.
  const menuItems = useMemo(
    () =>
      isAdminEmail(user?.email)
        ? [...BASE_MENU_ITEMS, ADMIN_MENU_ITEM]
        : BASE_MENU_ITEMS,
    [user?.email],
  );

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem(user?.uid!);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onMobileClose?.();
  };

  const { unreadCount } = useMyChats(user?.uid);
usePublicProfileSync(userData);

  return (
    <div
      className="dashboard-frame-surface"
      style={{
        width: collapsed ? 92 : 276,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
        borderRadius: 28,
        transition: "width 0.2s ease",
      }}
    >
      <div
        style={{
          paddingTop: 16,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            padding: "0 16px",
            display: "flex",
            justifyContent: collapsed ? "center" : "flex-end",
            marginBottom: 12,
          }}
        >
          {onToggleCollapse ? (
            <Button
              size="small"
              icon={collapsed ? <MenuUnfoldOutlined /> : <LeftOutlined />}
              onClick={onToggleCollapse}
              style={{
                borderRadius: 999,
                boxShadow: "0 2px 0 rgba(47,43,116,0.12)",
              }}
            />
          ) : null}
        </div>

        <div
          onClick={() => handleNavigation("/profile")}
          style={{ cursor: "pointer", marginBottom: 18 }}
        >
          <RewardedProfileDisplay
            src={sidebarAvatarSrc}
            alt="Perfil"
            fallbackSrc={profileIcon}
            width={collapsed ? 56 : 88}
            height={collapsed ? 56 : 88}
            border={`3px solid ${dashboardPalette.headerBlue}`}
            boxShadow="0 3px 0 rgba(47,43,116,0.25)"
            backgroundColor="#fff4c4"
            rewards={[...mergedRewards, ...(userData?.Items ?? [])]}
            overlayMode="full"
            overlayImageSx={{ top: -6 }}
            badgeSize={20}
            overlaySize={24}
          />
        </div>

        <div style={{ width: "100%", flex: 1, padding: "12px 0 16px" }}>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            inlineCollapsed={collapsed}
            style={{
              background: "transparent",
              borderInlineEnd: 0,
              width: "100%",
            }}
            onClick={(info) => handleNavigation(String(info.key))}
            items={menuItems.map((item) => ({
              key: item.path,
              icon: item.id === "chat"
  ? <Badge count={unreadCount} size="small"><item.icon style={{ fontSize: 20 }} /></Badge>
  : <item.icon style={{ fontSize: 20 }} />,
              label: collapsed ? null : item.label,
            }))}
          />

          <div style={{ padding: collapsed ? "4px 12px 0" : "4px 16px 0" }}>
            <Tooltip title="Contactanos" placement="right">
              <Button
                block
                href={buildGmailComposeUrl(
                  MANAGEMENT_EMAIL,
                  "Consulta Desafío",
                )}
                target="_blank"
                rel="noopener noreferrer"
                icon={<MailOutlined />}
                onClick={() => onMobileClose?.()}
                style={{
                  borderRadius: 16,
                  marginTop: 12,
                  background: "rgba(255,255,255,0.8)",
                }}
              >
                {collapsed ? null : "Contactanos"}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 16px 18px" }}>
        <Tooltip title="Cerrar sesión" placement="right">
          <Button
            block
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              borderRadius: 16,
              borderColor: dashboardPalette.panelBorder,
            }}
          >
            {collapsed ? null : "Salir"}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default Sidebar;
